import { useState, useCallback, useRef } from 'react';
import salesforceAccountsService from 'services/salesforceAccountsService';

const useInvestorCommitmentDetails = (accountId) => {
	const [detailsCache, setDetailsCache] = useState({});
	const [loadingStates, setLoadingStates] = useState({});
	const [errorStates, setErrorStates] = useState({});
	const cacheRef = useRef(detailsCache);
	cacheRef.current = detailsCache;

	const fetchDetails = useCallback(
		async (investorId) => {
			if (!accountId || !investorId) return;

			if (cacheRef.current[investorId]) {
				return cacheRef.current[investorId];
			}

			setLoadingStates((prev) => ({ ...prev, [investorId]: true }));
			setErrorStates((prev) => ({ ...prev, [investorId]: null }));

			try {
				const response =
					await salesforceAccountsService.getInvestorCommitmentDetails(
						accountId,
						investorId
					);
				
				// Log any warnings about missing field permissions
				if (response?.warnings && response.warnings.length > 0) {
					console.warn('Investor commitment details loaded with warnings:', {
						investorId,
						warnings: response.warnings,
					});
				}
				
				setDetailsCache((prev) => ({ ...prev, [investorId]: response }));
				return response;
			} catch (err) {
				const errorMessage = err?.message ?? 'Failed to load commitment details';
				setErrorStates((prev) => ({ ...prev, [investorId]: errorMessage }));
				console.error('Failed to fetch investor commitment details:', err);
				return null;
			} finally {
				setLoadingStates((prev) => ({ ...prev, [investorId]: false }));
			}
		},
		[accountId]
	);

	return {
		fetchDetails,
		getDetails: (investorId) => detailsCache[investorId] || null,
		isLoading: (investorId) => loadingStates[investorId] || false,
		getError: (investorId) => errorStates[investorId] || null,
	};
};

export default useInvestorCommitmentDetails;
